import React from 'react';
import styled, { css, keyframes } from 'styled-components';

/**
 * hover 시 --hoverImgURL이 설정되어 있으면 배경 이미지로 우선 적용.
 * --hoverImgURL이 없으면 none → 각 디자인 버튼 고유의 hover 효과가 유지됨.
 */
const hoverImgMixin = css`
  &:hover:not(:disabled) {
    cursor: var(--hoverCursorURL), pointer;
    background-image: var(--hoverImgURL, none) !important;
    background-size: cover !important;
    background-position: center !important;
  }
`;

const disabledMixin = css`
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export interface ButtonDesignProps {
  color?: string;
  backgroundColor?: string;
  pointColor?: string;
  children?: React.ReactNode;
  // Button 컴포넌트와 연동 시 전달되는 네이티브 버튼 props
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onMouseDown?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  style?: React.CSSProperties;
  block?: boolean;
  [key: string]: any;
}

// ─────────────────────────────────────────────
// 디자인 1: DiaButton  ↔ DiaArrow
// 다이아몬드 코너 장식 + 심플 사각형
// ─────────────────────────────────────────────
const DiaButtonWrap = styled.button<{ $color?: string; $bg?: string; $point?: string }>`
  --btn-color: ${(p) => p.$color || 'var(--buttonTextColor)'};
  --btn-bg: ${(p) => p.$bg || 'var(--buttonBackgroundColor)'};
  --btn-point: ${(p) => p.$point || p.$color || 'var(--buttonTextColor)'};

  position: relative;
  padding: 10px 28px;
  background: var(--btn-bg);
  color: var(--btn-color);
  border: 1px solid var(--btn-point);
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.05em;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  .dia-corner {
    position: absolute;
    width: 6px;
    height: 6px;
    background: var(--btn-point);
    transform: rotate(45deg);
    transition: all 0.25s ease;

    &.tl { top: -3px; left: -3px; }
    &.tr { top: -3px; right: -3px; }
    &.bl { bottom: -3px; left: -3px; }
    &.br { bottom: -3px; right: -3px; }
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);

    .dia-corner {
      box-shadow: 0 0 6px var(--btn-point);
    }
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }

  ${disabledMixin}
  ${hoverImgMixin}
`;

export const DiaButton: React.FC<ButtonDesignProps> = ({
  color, backgroundColor, pointColor, children = '버튼',
  onClick, onMouseDown, disabled, style, ...rest
}) => (
  <DiaButtonWrap
    $color={color} $bg={backgroundColor} $point={pointColor}
    onClick={onClick} onMouseDown={onMouseDown} disabled={disabled} style={style}
    {...rest}
  >
    <span className="dia-corner tl" />
    <span className="dia-corner tr" />
    <span className="dia-corner bl" />
    <span className="dia-corner br" />
    {children}
  </DiaButtonWrap>
);

// ─────────────────────────────────────────────
// 디자인 2: CircleButton  ↔ CircleArrow
// 알약형 + 양쪽 사이드 원형 장식 + 호버 fill
// ─────────────────────────────────────────────
const CircleButtonWrap = styled.button<{ $color?: string; $bg?: string; $point?: string }>`
  --btn-color: ${(p) => p.$color || 'var(--buttonTextColor)'};
  --btn-bg: ${(p) => p.$bg || 'var(--buttonBackgroundColor)'};
  --btn-point: ${(p) => p.$point || p.$color || 'var(--buttonTextColor)'};

  position: relative;
  padding: 10px 42px;
  background: var(--btn-bg);
  color: var(--btn-color);
  border: 1.5px solid var(--btn-point);
  border-radius: 50px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  overflow: hidden;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 0;

  .label {
    position: relative;
    z-index: 2;
    transition: color 0.35s ease;
  }

  /* 호버 fill 오버레이 */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50px;
    background: var(--btn-point);
    transform: scale(0);
    transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 0;
  }

  /* 양쪽 원형 장식 */
  .circle-ornament {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 1.5px solid var(--btn-point);
    background: var(--btn-bg);
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.35s ease, border-color 0.35s ease;

    &.left  { left: 10px; }
    &.right { right: 10px; }

    /* 내부 점 */
    &::after {
      content: '';
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: var(--btn-point);
      opacity: 0.5;
      transition: background 0.35s ease, opacity 0.35s ease;
    }
  }

  &:hover:not(:disabled) {
    &::before { transform: scale(2); }
    .label { color: var(--btn-bg, #fff); }
    .circle-ornament {
      background: transparent;
      border-color: rgba(255, 255, 255, 0.7);
      &::after { background: rgba(255, 255, 255, 0.7); opacity: 1; }
    }
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  ${disabledMixin}
  ${hoverImgMixin}
`;

export const CircleButton: React.FC<ButtonDesignProps> = ({
  color, backgroundColor, pointColor, children = '버튼',
  onClick, onMouseDown, disabled, style, ...rest
}) => (
  <CircleButtonWrap
    $color={color} $bg={backgroundColor} $point={pointColor}
    onClick={onClick} onMouseDown={onMouseDown} disabled={disabled} style={style}
    {...rest}
  >
    <span className="circle-ornament left" />
    <span className="circle-ornament right" />
    <span className="label">{children}</span>
  </CircleButtonWrap>
);

// ─────────────────────────────────────────────
// 디자인 3: SlideButton  ↔ SlideArrow
// 4모서리 챔퍼(팔각형) + 좌→우 파도 fill 애니메이션
// ─────────────────────────────────────────────
const C = 9; // chamfer 크기 (px)
const chamferPath = (c: number) =>
  `polygon(${c}px 0%, calc(100% - ${c}px) 0%, 100% ${c}px, 100% calc(100% - ${c}px), calc(100% - ${c}px) 100%, ${c}px 100%, 0% calc(100% - ${c}px), 0% ${c}px)`;

const SlideOuter = styled.div<{ $point?: string; $block?: boolean }>`
  --btn-point: ${(p) => p.$point || 'var(--buttonTextColor)'};
  display: ${({ $block }) => ($block ? 'flex' : 'inline-flex')};
  width: ${({ $block }) => ($block ? '100%' : 'auto')};
  align-items: stretch;
  background: var(--btn-point);
  clip-path: ${chamferPath(C)};
  padding: 1.5px;
  transition: filter 0.3s ease;

  &:hover:not(:has(:disabled)) {
    filter: drop-shadow(0 0 10px var(--btn-point));
  }

  &:has(:disabled) {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SlideInner = styled.button<{ $color?: string; $bg?: string; $point?: string }>`
  --btn-color: ${(p) => p.$color || 'var(--buttonTextColor)'};
  --btn-bg: ${(p) => p.$bg || 'var(--buttonBackgroundColor)'};
  --btn-point: ${(p) => p.$point || p.$color || 'var(--buttonTextColor)'};

  position: relative;
  padding: 10px 28px;
  background: var(--btn-bg);
  color: var(--btn-color);
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.05em;
  clip-path: ${chamferPath(C - 1.5)};
  overflow: hidden;
  transition: color 0.35s ease 0.05s;

  .label {
    position: relative;
    z-index: 1;
  }

  /* 파도 fill: 둥근 leading edge가 좌→우로 밀려옴 */
  &::before {
    content: '';
    position: absolute;
    top: -5%;
    left: -2px;
    width: 0;
    height: 110%;
    /* 오른쪽만 타원형으로 둥글게 → 파도 crest 효과 */
    border-radius: 0 55% 55% 0 / 0 50% 50% 0;
    background: var(--btn-point);
    transition:
      width 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94),
      border-radius 0.4s ease;
    z-index: 0;
  }

  &:hover:not(:disabled) {
    color: var(--btn-bg, #fff);

    &::before {
      width: 160%;
      border-radius: 0;
    }
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  ${disabledMixin}
  ${hoverImgMixin}
`;

export const SlideButton: React.FC<ButtonDesignProps> = ({
  color, backgroundColor, pointColor, children = '버튼',
  onClick, onMouseDown, disabled, style, block, ...rest
}) => (
  <SlideOuter $point={pointColor || color} $block={block}>
    <SlideInner
      $color={color} $bg={backgroundColor} $point={pointColor}
      onClick={onClick} onMouseDown={onMouseDown} disabled={disabled}
      style={{ width: block ? '100%' : undefined, ...style }}
      {...rest}
    >
      <span className="label">{children}</span>
    </SlideInner>
  </SlideOuter>
);

// ─────────────────────────────────────────────
// 디자인 4: RuneButton  ↔ RuneArrow
// 게임 UI - 메시 텍스처 + 코너 사각 마커 + 보더 글로우
// ─────────────────────────────────────────────
const runeShimmer = keyframes`
  0%        { left: -60%; }
  100%      { left: 130%; }
`;

const RuneButtonWrap = styled.button<{ $color?: string; $bg?: string; $point?: string }>`
  --btn-color: ${(p) => p.$color || '#d8d8d8'};
  --btn-bg: ${(p) => p.$bg || 'rgba(12, 16, 22, 0.92)'};
  --btn-point: ${(p) => p.$point || p.$color || 'rgba(220, 220, 220, 0.9)'};

  position: relative;
  padding: 12px 32px;
  /* 메시(격자) 텍스처 배경 */
  background-color: var(--btn-bg);
  background-image:
    repeating-linear-gradient(
      0deg,
      rgba(255, 255, 255, 0.025) 0px,
      rgba(255, 255, 255, 0.025) 1px,
      transparent 1px,
      transparent 6px
    ),
    repeating-linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.025) 0px,
      rgba(255, 255, 255, 0.025) 1px,
      transparent 1px,
      transparent 6px
    );
  color: var(--btn-color);
  border: 1px solid rgba(220, 220, 220, 0.55);
  border-radius: var(--buttonBorderRadius, 0);
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  box-shadow:
    0 0 8px rgba(220, 220, 220, 0.08),
    inset 0 0 18px rgba(0, 0, 0, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.07);

  /* 상단 하이라이트 라인 */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 10%;
    right: 10%;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.35) 30%,
      rgba(255, 255, 255, 0.35) 70%,
      transparent
    );
    pointer-events: none;
  }

  /* 호버 시 광택 스윕 오버레이 */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -60%;
    width: 50%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.14),
      transparent
    );
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  /* 코너 사각 마커 */
  .rune-mark {
    position: absolute;
    width: 6px;
    height: 6px;
    border: 1.5px solid var(--btn-point);
    background: transparent;
    opacity: 0.65;
    transition: opacity 0.2s ease, box-shadow 0.2s ease;

    &.tl { top: -4px;    left: -4px;    }
    &.tr { top: -4px;    right: -4px;   }
    &.bl { bottom: -4px; left: -4px;    }
    &.br { bottom: -4px; right: -4px;   }
  }

  &:hover:not(:disabled) {
    border-color: rgba(255, 255, 255, 0.9);
    box-shadow:
      0 0 22px rgba(255, 255, 255, 0.32),
      0 0 40px rgba(255, 255, 255, 0.10),
      inset 0 0 20px rgba(0, 0, 0, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.15);

    &::after {
      opacity: 1;
      animation: ${runeShimmer} 0.6s ease forwards;
    }

    .rune-mark {
      opacity: 1;
      box-shadow: 0 0 5px var(--btn-point);
    }
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow:
      0 0 8px rgba(220, 220, 220, 0.08),
      inset 0 0 18px rgba(0, 0, 0, 0.35),
      inset 0 1px 0 rgba(255, 255, 255, 0.07);
  }

  ${disabledMixin}
  ${hoverImgMixin}
`;

export const RuneButton: React.FC<ButtonDesignProps> = ({
  color, backgroundColor, pointColor, children = '버튼',
  onClick, onMouseDown, disabled, style, ...rest
}) => (
  <RuneButtonWrap
    $color={color} $bg={backgroundColor} $point={pointColor}
    onClick={onClick} onMouseDown={onMouseDown} disabled={disabled} style={style}
    {...rest}
  >
    <span className="rune-mark tl" />
    <span className="rune-mark tr" />
    <span className="rune-mark bl" />
    <span className="rune-mark br" />
    {children}
  </RuneButtonWrap>
);

// ─────────────────────────────────────────────
// 디자인 5: PixelButton  ↔ PixelArrow
// 레트로 픽셀 + 오프셋 박스 섀도
// ─────────────────────────────────────────────
const PixelButtonWrap = styled.button<{ $color?: string; $bg?: string; $point?: string }>`
  --btn-color: ${(p) => p.$color || 'var(--buttonTextColor)'};
  --btn-bg: ${(p) => p.$bg || 'var(--buttonBackgroundColor)'};
  --btn-point: ${(p) => p.$point || p.$color || 'var(--buttonTextColor)'};

  position: relative;
  padding: 10px 24px;
  background: var(--btn-bg);
  color: var(--btn-color);
  border: 3px solid var(--btn-point);
  border-radius: 0;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.08em;
  image-rendering: pixelated;
  box-shadow: 4px 4px 0 0 var(--btn-point);
  transition:
    box-shadow 0.08s steps(2),
    transform 0.08s steps(2);

  &:hover:not(:disabled) {
    box-shadow: 1px 1px 0 0 var(--btn-point);
    transform: translate(3px, 3px);
  }

  &:active:not(:disabled) {
    box-shadow: none;
    transform: translate(4px, 4px);
  }

  ${disabledMixin}
  ${hoverImgMixin}
`;

export const PixelButton: React.FC<ButtonDesignProps> = ({
  color, backgroundColor, pointColor, children = '버튼',
  onClick, onMouseDown, disabled, style, ...rest
}) => (
  <PixelButtonWrap
    $color={color} $bg={backgroundColor} $point={pointColor}
    onClick={onClick} onMouseDown={onMouseDown} disabled={disabled} style={style}
    {...rest}
  >
    {children}
  </PixelButtonWrap>
);

// ─────────────────────────────────────────────
// 디자인 6: CrystalButton  ↔ CrystalArrow
// 라운드 렉탱글 + 양쪽 다이아몬드 사이드 장식 + 글로우
// ─────────────────────────────────────────────
const crystalPulse = keyframes`
  0%, 100% { opacity: 0.3; }
  50%       { opacity: 0.6; }
`;

const CrystalOuter = styled.div<{ $color?: string; $bg?: string; $point?: string; $block?: boolean }>`
  --btn-color: ${(p) => p.$color || 'var(--buttonTextColor)'};
  --btn-bg: ${(p) => p.$bg || 'var(--buttonBackgroundColor)'};
  --btn-point: ${(p) => p.$point || p.$color || 'var(--buttonTextColor)'};

  position: relative;
  display: ${({ $block }) => ($block ? 'block' : 'inline-block')};
  width: ${({ $block }) => ($block ? '100%' : 'fit-content')};

  /* 양쪽 다이아몬드 장식 */
  .diamond-ornament {
    position: absolute;
    top: 50%;
    width: 8px;
    height: 8px;
    transform: translateY(-50%) rotate(45deg);
    border: 1px solid var(--btn-point);
    background: var(--btn-bg);
    z-index: 2;
    transition: box-shadow 0.3s ease;

    &.left  { left: -4px; }
    &.right { right: -4px; }

    /* 내부 채움 */
    &::before {
      content: '';
      position: absolute;
      inset: 1px;
      background: var(--btn-point);
      opacity: 0.25;
      transition: opacity 0.3s ease;
    }
  }

  &:hover:not(:has(:disabled)) .diamond-ornament {
    box-shadow: 0 0 8px var(--btn-point);
    &::before { opacity: 0.85; }
  }

  &:has(:disabled) {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CrystalInner = styled.button<{ $color?: string; $bg?: string; $point?: string }>`
  --btn-color: ${(p) => p.$color || 'var(--buttonTextColor)'};
  --btn-bg: ${(p) => p.$bg || 'var(--buttonBackgroundColor)'};
  --btn-point: ${(p) => p.$point || p.$color || 'var(--buttonTextColor)'};

  position: relative;
  padding: 10px 28px;
  background: var(--btn-bg);
  color: var(--btn-color);
  border: 1px solid var(--btn-point);
  border-radius: var(--buttonBorderRadius, 8px);
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.05em;
  transition: all 0.3s ease;
  z-index: 1;

  /* 맥동 외곽 링 */
  &::before {
    content: '';
    position: absolute;
    inset: -5px;
    border: 1px solid var(--btn-point);
    border-radius: calc(var(--buttonBorderRadius, 8px) + 4px);
    opacity: 0.25;
    animation: ${crystalPulse} 3s ease-in-out infinite;
    pointer-events: none;
  }

  &:hover:not(:disabled) {
    box-shadow: 0 0 14px var(--btn-point);
    &::before { opacity: 0.7; }
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 0 6px var(--btn-point);
  }

  ${disabledMixin}
  ${hoverImgMixin}
`;

export const CrystalButton: React.FC<ButtonDesignProps> = ({
  color, backgroundColor, pointColor, children = '버튼',
  onClick, onMouseDown, disabled, style, block, ...rest
}) => (
  <CrystalOuter $color={color} $bg={backgroundColor} $point={pointColor} $block={block}>
    <span className="diamond-ornament left" />
    <span className="diamond-ornament right" />
    <CrystalInner
      $color={color} $bg={backgroundColor} $point={pointColor}
      onClick={onClick} onMouseDown={onMouseDown} disabled={disabled}
      style={{ width: block ? '100%' : undefined, ...style }}
      {...rest}
    >
      {children}
    </CrystalInner>
  </CrystalOuter>
);

// ─────────────────────────────────────────────
// 기본 버튼 (디자인 미적용 · 버튼 테마 설정을 그대로 따름)
// ─────────────────────────────────────────────
const DefaultButtonEl = styled.button<{ $block?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 20px;
  width: ${({ $block }) => ($block ? '100%' : 'auto')};
  background: var(--buttonBackgroundColor);
  color: var(--buttonTextColor);
  border: 1px solid var(--lineColor);
  border-radius: var(--buttonBorderRadius, 8px);
  font-size: 14px;
  font-weight: 600;
  cursor: var(--hoverCursorURL), pointer;
  transition: opacity 0.15s;

  &:hover:not(:disabled) {
    opacity: 0.8;
  }
  ${disabledMixin}
  ${hoverImgMixin}
`;

export const DefaultButton: React.FC<ButtonDesignProps> = ({
  children = '버튼',
  onClick, onMouseDown, disabled, style, block, ...rest
}) => (
  <DefaultButtonEl
    $block={block}
    onClick={onClick}
    onMouseDown={onMouseDown}
    disabled={disabled}
    style={style}
    {...rest}
  >
    {children}
  </DefaultButtonEl>
);
